import {ChevronDown} from "lucide-react"
import type React from "react"

import {cn} from "@/lib/cn"

/**
 * Shared field surface: the `h-10 w-full` metrics, rounded-mini bordered
 * surface, and focus-visible ring every control on a form row needs to line
 * up. `Input` applies it to a native `<input>`; `DateRangeFields`'s popover
 * trigger (`date-range-fields.tsx`) applies it to a `<button>` so the trigger
 * reads as a field, not a pill button parked in a form row.
 */
export const fieldSurfaceClassName = [
  "flex h-10 w-full rounded-mini border border-secondary-border bg-background/60 px-3 py-2",
  "font-sans text-sm text-foreground placeholder:text-muted-foreground",
  "transition-colors duration-150",
  "focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive",
].join(" ")

/** DIZED text input: soft radius, lit focus ring, theme-aware surface. */
export const Input = ({
  className,
  type,
  ref,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {ref?: React.Ref<HTMLInputElement>}) => (
  <input ref={ref} type={type} className={cn(fieldSurfaceClassName, className)} {...props} />
)

/** DIZED multiline input. */
export const Textarea = ({
  className,
  ref,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {ref?: React.Ref<HTMLTextAreaElement>}) => (
  <textarea
    ref={ref}
    className={cn(
      "flex min-h-20 w-full rounded-mini border border-secondary-border bg-background/60 px-3 py-2",
      "font-sans text-sm text-foreground placeholder:text-muted-foreground",
      "focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  />
)

/**
 * A native checkbox, not a Radix one.
 *
 * Every form in the game flows is a server action, so the inputs have to submit
 * with the form. A Radix checkbox is a button plus a hidden input driven by
 * client state; a native one posts its value with no JavaScript at all, which
 * also means these forms still work while the page is hydrating.
 */
export const Checkbox = ({className, ...props}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    type="checkbox"
    className={cn(
      "size-4 shrink-0 rounded-[4px] border border-secondary-border bg-background/60",
      "accent-[color:var(--color-link)]",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  />
)

/** Form label: small bold uppercase, per the style guide's form rows. */
export const Label = ({className, ...props}: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: callers pass htmlFor.
  <label
    className={cn(
      "font-sans text-xs font-bold uppercase tracking-wide text-muted-foreground",
      className,
    )}
    {...props}
  />
)

/**
 * A labelled field with room for help text and an error.
 *
 * Exists so the error is always rendered in the same place relative to the
 * input and always wired to it by `aria-describedby` — the part that is easy to
 * forget per-field and invisible when it is missing.
 */
export const Field = ({
  id,
  label,
  hint,
  error,
  required = false,
  children,
}: {
  id: string
  label: string
  hint?: string | undefined
  error?: string | undefined
  /** Marks the label with an asterisk; the input still carries `required` itself. */
  required?: boolean
  children: React.ReactNode
}) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>
      {label}
      {required && (
        <span aria-hidden className="ml-0.5 text-destructive">
          *
        </span>
      )}
    </Label>
    {children}
    {hint !== undefined && error === undefined && (
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {hint}
      </p>
    )}
    {error !== undefined && (
      <p id={`${id}-error`} className="text-xs text-destructive">
        {error}
      </p>
    )}
  </div>
)

/**
 * A native `<select>`, not the Radix one in ./select.tsx.
 *
 * Same reasoning as `Checkbox` above: every form in the game flows is a server
 * action, so the control has to submit with the form and keep working before
 * hydration. Radix's Select renders a hidden native input for form
 * compatibility, but the visible control is JavaScript-driven — so on a slow
 * connection the publisher sees a dropdown that does not open yet.
 *
 * Named `NativeSelect` because `Select` is the Radix component, which is still
 * the right choice for a rich picker that is not part of a posted form.
 *
 * `appearance-none` strips the browser's own arrow, so the chevron is drawn
 * back in. Without it the control read as a button that adds whatever it
 * happens to show (portal feedback, 2026-10). `className` sizes the WRAPPER,
 * so the chevron follows the select's width.
 */
export const NativeSelect = ({
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <span className={cn("relative block w-full", className)}>
    <select
      className={cn(
        "flex h-10 w-full appearance-none rounded-mini border border-secondary-border bg-background/60 py-2 pr-9 pl-3",
        "font-sans text-sm text-foreground",
        "focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
      )}
      {...props}
    >
      {children}
    </select>
    <ChevronDown
      aria-hidden
      className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
    />
  </span>
)
